import React, { useState, useEffect, useMemo } from 'react';
import negocioService from '../services/negocio.service';

// ═══════════════════════════════════════════════════════════════════════════════
// Modal de alteração de etapa comercial com regras de negócio:
//   • Combo mostra apenas etapas adjacentes (anterior + próxima)
//   • Botão separado "Marcar como Perdido" pra etapa fora do fluxo (D)
//   • Campo de data (default hoje, editável)
//   • Ao retroagir: pergunta se atualiza data da etapa de destino
// ═══════════════════════════════════════════════════════════════════════════════

const COR = {
  azul: '#003399', azulBg: '#eff6ff',
  branco: '#fff', borda: '#e8eaed',
  texto: '#1a1a2e', muted: '#888',
  cinzaBg: '#fafafa',
  vermelho: '#e53935', vermBg: '#fff0f0',
  verde: '#1D9E75', verdeBg: '#f0fff4',
  amarelo: '#d97706', amlBg: '#fffbeb',
};

// Sequência linear (Perdido fora)
const ORDEM_ETAPAS = ['O', 'L', 'P', 'A', 'F'];

const ETAPA_INFO = {
  'O': { nome: 'Oportunidade', cor: '#6b7280' },
  'L': { nome: 'Levantamento', cor: '#0891b2' },
  'P': { nome: 'Proposta',     cor: '#d97706' },
  'A': { nome: 'Aprovação',    cor: '#7c3aed' },
  'F': { nome: 'Fechado',      cor: '#1D9E75' },
  'E': { nome: 'Encerrado',    cor: '#059669' },
  'D': { nome: 'Perdido',      cor: '#e53935' },
};

// Etapas técnicas (em ordem)
const ETAPAS_TECNICAS = [
  'Planejamento', 'Execução', 'Qualidade',
  'Validação Cliente', 'Acompanhamento', 'Encerrado',
];

const inputStyle = {
  width: '100%', height: '40px', padding: '0 12px',
  border: `1px solid ${COR.borda}`, borderRadius: '5px',
  fontSize: '15px', boxSizing: 'border-box',
  backgroundColor: COR.branco, color: COR.texto, outline: 'none',
  fontFamily: "'Manrope', sans-serif",
};

const selectStyle = { ...inputStyle, cursor: 'pointer' };

function hojeISO() {
  return new Date().toISOString().slice(0, 10);
}

function dataParaInput(v) {
  if (!v) return '';
  const d = new Date(v);
  return isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
}

// ═══════════════════════════════════════════════════════════════════════════════
function AlterarEtapaModal({
  aberto, negocio, usuarioId, onFechar, onSucesso,
  // Quando setado, pré-seleciona uma etapa e bloqueia o combo
  // (usado pra fluxo automático tipo "DOS010 sugere ir pra Proposta")
  etapaPreSelecionada = null,
  motivoPreenchido = '',
  // Quando true, NÃO chama backend ao avançar pra F: chama apenas onSucesso
  // passando os dados (data, motivo) pra o componente pai abrir o modal de
  // Fechamento. Usado pra coletar dados de fechamento antes de salvar.
  interceptarFechamento = false,
  // Quando true, troca o modo do modal pra alterar ETAPA TÉCNICA do projeto.
  // Combo mostra etapas técnicas; salva chamando alterarEtapaTecnica.
  modoTecnico = false,
  // ID do anexo recém-incluído que disparou esse modal (DOS010 que sugere
  // mudança de etapa). Se passado, o backend sincroniza a data do evento
  // desse anexo com a data escolhida pra etapa.
  anexoIdRecente = null,
}) {
  const [novaEtapa, setNovaEtapa] = useState('');
  const [dataEvento, setDataEvento] = useState(hojeISO());
  const [motivo, setMotivo] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  // Estado pro caso de retroagir
  const [perguntaDataAnterior, setPerguntaDataAnterior] = useState(false);
  const [atualizarDataAnterior, setAtualizarDataAnterior] = useState(false);
  const [dataAnteriorNova, setDataAnteriorNova] = useState('');

  const etapaAtual = modoTecnico ? negocio?.etapa_tecnica : negocio?.etapa_comercial;

  // Calcula opções disponíveis no combo (etapas adjacentes)
  const opcoesAdjacentes = useMemo(() => {
    // ─── MODO TÉCNICO ───
    // Lista todas as etapas técnicas como opções (livre escolha,
    // não tem regra de adjacência por enquanto)
    if (modoTecnico) {
      return ETAPAS_TECNICAS
        .filter(e => e !== etapaAtual)
        .map(e => ({ valor: e, info: { nome: e, cor: '#003399' }, tipo: 'tecnico' }));
    }

    // ─── MODO COMERCIAL (original) ───
    if (!etapaAtual) return [];

    // Se está em Perdido, todas as etapas viram opções (volta a qualquer ponto do fluxo)
    if (etapaAtual === 'D') {
      return ORDEM_ETAPAS.map(e => ({ valor: e, info: ETAPA_INFO[e] }));
    }

    const idx = ORDEM_ETAPAS.indexOf(etapaAtual);
    if (idx === -1) return [];

    const opcoes = [];
    if (idx > 0) opcoes.push({
      valor: ORDEM_ETAPAS[idx - 1],
      info: ETAPA_INFO[ORDEM_ETAPAS[idx - 1]],
      tipo: 'retro',
    });
    if (idx < ORDEM_ETAPAS.length - 1) opcoes.push({
      valor: ORDEM_ETAPAS[idx + 1],
      info: ETAPA_INFO[ORDEM_ETAPAS[idx + 1]],
      tipo: 'avanco',
    });
    return opcoes;
  }, [etapaAtual, modoTecnico]);

  // Reseta o form ao abrir
  useEffect(() => {
    if (aberto) {
      setNovaEtapa(etapaPreSelecionada || '');
      setDataEvento(hojeISO());
      setMotivo(motivoPreenchido || '');
      setPerguntaDataAnterior(false);
      setAtualizarDataAnterior(false);
      setDataAnteriorNova('');
      setErro('');
    }
  }, [aberto, etapaPreSelecionada, motivoPreenchido]);

  // Quando o usuário escolhe uma etapa, detecta se é retroagir
  useEffect(() => {
    if (!novaEtapa || !etapaAtual) {
      setPerguntaDataAnterior(false);
      return;
    }

    // Modo técnico: não tem regra de retroagir/avançar (livre escolha)
    if (modoTecnico) {
      setPerguntaDataAnterior(false);
      setAtualizarDataAnterior(false);
      return;
    }

    const idxAtual = ORDEM_ETAPAS.indexOf(etapaAtual);
    const idxNova  = ORDEM_ETAPAS.indexOf(novaEtapa);

    if (etapaAtual !== 'D' && novaEtapa !== 'D'
        && idxAtual >= 0 && idxNova >= 0 && idxNova < idxAtual) {
      // Retroagindo!
      setPerguntaDataAnterior(true);
      // Default: pré-preenche data anterior com hoje (se atualizar)
      setDataAnteriorNova(hojeISO());
    } else {
      setPerguntaDataAnterior(false);
      setAtualizarDataAnterior(false);
    }
  }, [novaEtapa, etapaAtual, modoTecnico]);

  // ─── Salvar (avanço/retroagimento) ────────────────────────────────────────
  const salvarAlteracao = async (etapaForcada) => {
    setErro('');
    const etapaFinal = etapaForcada || novaEtapa;

    if (!etapaFinal) {
      return setErro('Selecione a nova etapa.');
    }
    if (!dataEvento) {
      return setErro('Informe a data da alteração.');
    }

    // ═══ MODO TÉCNICO ═══
    // Chama endpoint diferente: alterarEtapaTecnica
    if (modoTecnico) {
      setSalvando(true);
      try {
        const payload = {
          nova_etapa_tecnica: etapaFinal,
          data_evento: dataEvento,
          motivo: motivo.trim() || undefined,
          usuario_id: usuarioId,
        };
        await negocioService.alterarEtapaTecnica(negocio.id, payload);
        onSucesso && onSucesso(etapaFinal);
        onFechar();
      } catch (err) {
        console.error('Erro ao alterar etapa técnica:', err);
        setErro(err.response?.data?.mensagem || 'Erro ao alterar etapa técnica.');
      } finally {
        setSalvando(false);
      }
      return;
    }

    // ═══ INTERCEPTAR FECHAMENTO ═══
    // Se quem chamou pediu pra interceptar e o usuário escolheu ir pra F,
    // NÃO chama o backend aqui. Repassa os dados pra o pai abrir o
    // FecharNegocioModal. Quando os dados de fechamento vierem, aí sim
    // o pai chamará alterarEtapaComMotivo com o body completo.
    if (interceptarFechamento && etapaFinal === 'F') {
      // Detecta direção (avanço x retroceder; retroagir não intercepta)
      const idxAtual = negocio?.etapa_comercial
        ? ['O','L','P','A','F'].indexOf(negocio.etapa_comercial) : -1;
      const idxNova  = ['O','L','P','A','F'].indexOf('F');
      const ehAvanco = idxAtual >= 0 && idxNova > idxAtual;
      if (ehAvanco) {
        onSucesso && onSucesso(etapaFinal, {
          direcao: 'avanco',
          data_evento: dataEvento,
          motivo: motivo.trim() || `Etapa alterada para Fechado`,
        });
        // Não fecha o modal aqui — o pai fecha após processar
        return;
      }
    }

    setSalvando(true);
    try {
      const payload = {
        nova_etapa: etapaFinal,
        data_evento: dataEvento,
        motivo: motivo.trim() || undefined,
        usuario_id: usuarioId,
        atualizar_data_anterior: atualizarDataAnterior,
        data_anterior_nova: atualizarDataAnterior ? dataAnteriorNova : undefined,
        // Se o modal foi aberto por causa de um anexo recém-incluído (ex: DOS010
        // que sugere mudança de etapa), passa o id pro backend sincronizar a
        // data do evento do anexo com a data escolhida pra etapa.
        anexo_id_recente: anexoIdRecente || undefined,
      };
      await negocioService.alterarEtapaModal(negocio.id, payload);
      onSucesso && onSucesso(etapaFinal);
      onFechar();
    } catch (err) {
      console.error('Erro ao alterar etapa:', err);
      setErro(err.response?.data?.mensagem || 'Erro ao alterar etapa.');
    } finally {
      setSalvando(false);
    }
  };

  // ─── Marcar como Perdido (botão separado) ─────────────────────────────────
  const marcarPerdido = async () => {
    if (!motivo.trim()) {
      setErro('Para marcar como Perdido, informe o motivo no campo "Motivo / Observação".');
      // Tenta focar no textarea pra direcionar o usuário
      const ta = document.querySelector('textarea');
      if (ta) ta.focus();
      return;
    }
    if (motivo.trim().length < 5) {
      setErro('O motivo deve ter pelo menos 5 caracteres.');
      return;
    }
    if (!window.confirm(
      'Tem certeza que deseja marcar este negócio como Perdido?\n\n' +
      'Após confirmação, o negócio não poderá ser editado normalmente.'
    )) return;
    // Força nova_etapa = 'D' e usa a data informada (ou hoje)
    setNovaEtapa('D');
    await salvarAlteracao('D');
  };

  if (!aberto || !negocio) return null;

  const Z_OVERLAY = 10000;
  const Z_MODAL   = 10001;

  // No modo técnico, etapaAtual é uma string ('Planejamento', etc.) — usamos
  // ela direto. No modo comercial, é uma letra que mapeamos via ETAPA_INFO.
  const infoAtual = modoTecnico
    ? { nome: etapaAtual || 'Planejamento', cor: '#003399' }
    : (ETAPA_INFO[etapaAtual] || {});
  const podeMarcarPerdido = !modoTecnico && etapaAtual !== 'D' && etapaAtual !== 'F';

  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <>
      {/* r133: overlay sem onClick — fechar so pelos botoes */}
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)', zIndex: Z_OVERLAY,
      }} />

      <div style={{
        position: 'fixed', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '92%', maxWidth: '640px', maxHeight: '92vh',
        backgroundColor: COR.branco, borderRadius: '8px',
        boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
        display: 'flex', flexDirection: 'column',
        zIndex: Z_MODAL, overflow: 'hidden',
        fontFamily: "'Manrope', sans-serif",
      }}>
        {/* Header */}
        <div style={{
          padding: '14px 20px', borderBottom: `1px solid ${COR.borda}`,
          display: 'flex', alignItems: 'center', gap: '12px',
          backgroundColor: COR.azul, color: COR.branco,
        }}>
          <span style={{ fontSize: '18px' }}>{modoTecnico ? '⚙️' : '🔄'}</span>
          <span style={{ fontSize: '15px', fontWeight: '600', flex: 1 }}>
            {modoTecnico ? 'Alterar Etapa Técnica' : 'Alterar Etapa Comercial'}
          </span>
          <button onClick={onFechar} style={{
            border: 'none', background: 'transparent',
            color: COR.branco, fontSize: '20px', cursor: 'pointer',
          }}>×</button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '22px 28px' }}>
          {erro && (
            <div style={{
              backgroundColor: COR.vermBg, border: '1px solid #ffcccc',
              borderRadius: '5px', color: COR.vermelho, fontSize: '12px',
              padding: '8px 10px', marginBottom: '12px',
            }}>{erro}</div>
          )}

          {/* Etapa atual */}
          <div style={{ marginBottom: '20px' }}>
            <label style={LabelStyle}>Etapa atual</label>
            <div style={{
              padding: '12px 16px', borderRadius: '6px',
              backgroundColor: infoAtual.cor + '22',
              color: infoAtual.cor,
              fontSize: '15px', fontWeight: '700',
              textAlign: 'center',
              border: `1px solid ${infoAtual.cor}`,
            }}>
              {infoAtual.nome || etapaAtual}
            </div>
          </div>

          {/* Combo nova etapa */}
          <div style={{ marginBottom: '20px' }}>
            <label style={LabelStyle}>Nova etapa</label>
            {opcoesAdjacentes.length === 0 ? (
              <div style={{
                padding: '10px', backgroundColor: COR.cinzaBg,
                borderRadius: '5px', fontSize: '12px', color: COR.muted,
                textAlign: 'center',
              }}>
                Não há etapas adjacentes disponíveis.
                {etapaAtual === 'F' && ' Negócio fechado.'}
              </div>
            ) : (
              <select value={novaEtapa}
                onChange={e => setNovaEtapa(e.target.value)}
                disabled={!!etapaPreSelecionada}
                style={etapaPreSelecionada
                  ? { ...selectStyle, backgroundColor: COR.cinzaBg, cursor: 'not-allowed' }
                  : selectStyle}>
                <option value="">Selecione...</option>
                {opcoesAdjacentes.map(o => (
                  <option key={o.valor} value={o.valor}>
                    {o.tipo === 'avanco'  ? '➡️ Avançar para' :
                     o.tipo === 'retro'   ? '⬅️ Retroagir para' :
                     o.tipo === 'tecnico' ? '⚙️ Mudar para' :
                     '🔁 Voltar para'} {o.info.nome}
                  </option>
                ))}
              </select>
            )}
            {etapaPreSelecionada && (
              <div style={{ fontSize: '11px', color: COR.amarelo, marginTop: '4px', fontWeight: '500' }}>
                ℹ️ Etapa sugerida automaticamente após inclusão de anexo.
              </div>
            )}
          </div>

          {/* Data do evento */}
          <div style={{ marginBottom: '20px' }}>
            <label style={LabelStyle}>Data da alteração</label>
            <input type="date" value={dataEvento}
              onChange={e => setDataEvento(e.target.value)}
              style={inputStyle} />
            <div style={{ fontSize: '11px', color: COR.muted, marginTop: '4px' }}>
              Sugerimos a data de hoje, mas você pode alterar se a etapa aconteceu em outra data.
            </div>
          </div>

          {/* Motivo */}
          <div style={{ marginBottom: '20px' }}>
            <label style={LabelStyle}>
              Motivo / Observação
              {' '}
              <span style={{ color: COR.muted, fontWeight: '400', textTransform: 'none', letterSpacing: '0' }}>
                (obrigatório para "Marcar como Perdido")
              </span>
            </label>
            <textarea value={motivo}
              onChange={e => setMotivo(e.target.value)}
              placeholder="Descreva a justificativa da mudança"
              maxLength={500}
              style={{
                ...inputStyle, height: '80px', padding: '10px 12px',
                resize: 'vertical', fontFamily: 'inherit',
              }} />
          </div>

          {/* Pergunta de retroagir */}
          {perguntaDataAnterior && (
            <div style={{
              backgroundColor: COR.amlBg, border: `1px solid #f0c97a`,
              borderRadius: '6px', padding: '16px', marginBottom: '20px',
            }}>
              <div style={{ fontSize: '13px', fontWeight: '600', color: COR.amarelo, marginBottom: '10px' }}>
                ⚠️ Atenção — você está retroagindo
              </div>
              <div style={{ fontSize: '12px', color: COR.texto, marginBottom: '12px', lineHeight: '1.5' }}>
                O evento da etapa atual será removido da timeline.
                Deseja atualizar também a data de início da etapa anterior?
              </div>

              <label style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                fontSize: '13px', color: COR.texto,
                cursor: 'pointer', userSelect: 'none', marginBottom: '10px',
              }}>
                <input type="checkbox" checked={atualizarDataAnterior}
                  onChange={e => setAtualizarDataAnterior(e.target.checked)}
                  style={{ cursor: 'pointer' }} />
                Sim, atualizar data da etapa anterior
              </label>

              {atualizarDataAnterior && (
                <input type="date" value={dataAnteriorNova}
                  onChange={e => setDataAnteriorNova(e.target.value)}
                  style={inputStyle} />
              )}
              {!atualizarDataAnterior && (
                <div style={{ fontSize: '11px', color: COR.muted, marginTop: '4px' }}>
                  A data registrada anteriormente será mantida.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 28px', borderTop: `1px solid ${COR.borda}`,
          display: 'flex', gap: '10px', alignItems: 'center',
          backgroundColor: COR.cinzaBg,
        }}>
          {/* Botão Marcar como Perdido (esquerda) */}
          {podeMarcarPerdido && (
            <button onClick={marcarPerdido} disabled={salvando} style={{
              padding: '10px 16px', fontSize: '13px', fontWeight: '600',
              border: `1px solid ${COR.vermelho}`, borderRadius: '5px',
              backgroundColor: COR.vermBg, color: COR.vermelho,
              cursor: salvando ? 'not-allowed' : 'pointer',
              opacity: salvando ? 0.6 : 1,
            }}>
              ❌ Marcar como Perdido
            </button>
          )}

          <div style={{ flex: 1 }} />

          {/* Botão Cancelar */}
          <button onClick={onFechar} disabled={salvando} style={{
            padding: '10px 16px', fontSize: '13px',
            border: `1px solid ${COR.borda}`, borderRadius: '5px',
            backgroundColor: COR.branco, color: COR.texto, cursor: 'pointer',
          }}>Cancelar</button>

          {/* Botão Salvar */}
          {opcoesAdjacentes.length > 0 && (
            <button onClick={() => salvarAlteracao()}
              disabled={salvando || !novaEtapa}
              style={{
                padding: '10px 22px', fontSize: '13px', fontWeight: '600',
                border: 'none', borderRadius: '5px',
                backgroundColor: COR.azul, color: COR.branco,
                cursor: (salvando || !novaEtapa) ? 'not-allowed' : 'pointer',
                opacity: (salvando || !novaEtapa) ? 0.5 : 1,
              }}>
              {salvando ? 'Salvando...' : 'Confirmar alteração'}
            </button>
          )}
        </div>
      </div>
    </>
  );
}

const LabelStyle = {
  display: 'block', fontSize: '11px', fontWeight: '600',
  color: COR.muted, marginBottom: '4px',
  textTransform: 'uppercase', letterSpacing: '0.4px',
};

export default AlterarEtapaModal;
